import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { ExpenseManagerReviewStatus } from 'src/models/expenseReport.model'
import {
  ExpenseReviewResponse,
  TransactionFinalApprovalStatus,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { MutationMethod, useExtendMutation } from 'src/queries/queries.utils'
import { updateTransactionDetailResponse } from 'src/queries/transactions.queries'
import { PATCH_REIMBURSEMENT_MANAGER_APPROVAL } from 'src/redux/actions/data/expenses.actions'

export const usePatchTransactionManagerReview = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ExpenseReviewResponse[],
    {
      body: {
        transactionIds: string[]
        managerApprovalStatus: TransactionManagerReviewStatus
      }
      filters: TransactionsFiltersBody
    }
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/expensereview/transaction/managerapproval`,
    onSuccess: (data, variables) => {
      variables.body.transactionIds.forEach((transactionId) => {
        updateTransactionDetailResponse(queryClient, {
          transactionId: transactionId,
          updater: (old) => ({
            ...old,
            managerApprovalStatus: variables.body.managerApprovalStatus,
          }),
        })
      })
      // invalidate the list query for the current filters to remove transactions from NeedsApproval list
      // (we cant just remove them since more transactions may need to be fetched)
      queryClient.invalidateQueries({
        queryKey: transactionsQueryKeys.list(variables.filters),
      })
      // also invalidate count on header tab
      queryClient.invalidateQueries({
        queryKey: transactionsQueryKeys.count({ needsManagerApproval: true }),
      })
    },
  })
}

export const usePatchTransactionFinalApprovalStatus = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ExpenseReviewResponse[],
    {
      body: {
        transactionIds: string[]
        finalApprovalStatus: TransactionFinalApprovalStatus
      }
      filters?: TransactionsFiltersBody
      invalidateFilters?: TransactionsFiltersBody
    }
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/expensereview/transaction/finalapproval`,
    onSuccess: (data, variables) => {
      variables.body.transactionIds.forEach((transactionId) => {
        updateTransactionDetailResponse(queryClient, {
          transactionId: transactionId,
          updater: (old) => ({
            ...old,
            finalApprovalStatus: variables.body.finalApprovalStatus,
          }),
        })
      })
      // invalidate the list query for the current filters to remove transactions from NeedsApproval list
      // (we cant just remove them since more transactions may need to be fetched)
      queryClient.invalidateQueries({
        // if filters are provided, use those to invalidate, otherwise use invalidateFilters
        queryKey: transactionsQueryKeys.list(
          variables.filters || variables.invalidateFilters,
        ),
      })
      // also invalidate count on header tab
      queryClient.invalidateQueries({
        queryKey: transactionsQueryKeys.count({ needsFinalApproval: true }),
      })
    },
  })
}

export interface ManagerApprovalReimbursementResponse {
  id: string
  reportId: string
  managerReviewStatus: ExpenseManagerReviewStatus
  managerId: string
}

export const usePatchReimbursementManagerReview = () => {
  const dispatch = useDispatch()
  return useExtendMutation<
    ManagerApprovalReimbursementResponse[],
    {
      body: {
        reimbursementIds: string[]
        managerApprovalStatus: ExpenseManagerReviewStatus
      }
    }
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/expensereview/reimbursement/managerapproval`,
    onSuccess: (data) => {
      dispatch({ type: PATCH_REIMBURSEMENT_MANAGER_APPROVAL, payload: data })
    },
  })
}
